import {
ButtonType, ButtonWidget,   DialogFooterButtonsWidget,
DropdownButtonType, List, ListItem,   SingleSelectDropdown,   TextLinkKind,
  TextLinkWidget,
Tooltip, TooltipPosition
} from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import {imgUrl} from '~common/utilities/ImgUrl';

import { Messages } from '../../codegen/Localize';
import { IDataSourceBase } from '../../datasource/models/IDataSourceInfo';
import {WebHttpClientWithErrorToastInstance} from '../../httpClient/WebHttpClientWithErrorToast';
import {ConnectionTypes} from '../../settings/components/AnalyticsExtensionsSettingsForSite';
import {
  IAnalyticsConnectionBrief,
  IAnalyticsConnectionMapping,
  IAnalyticsConnectionMetadata
} from '../../settings/types/ISiteSettingsForSiteAdmin';
import { IContentInfo } from '../../ts/models/IContentInfo';
import {GetHasScriptCalcRequest} from '../../ts/ServerApi/requests/GetHasScriptCalcRequest';
import {
  GetAnalyticsConnectionOptionsForWorkbookRequest,
  IGetAnalyticsConnectionOptionsForWorkbookResult
} from '../../ts/ServerApi/requests/settings/extensions/analytics/GetAnalyticsConnectionOptionsForWorkbookRequest';
import {RemoveAnalyticsExtConnectionForWorkbookRequest} from '../../workbook/ServerApi/requests/RemoveAnalyticsExtConnectionForWorkbookRequest';
import {SetAnalyticsExtConnectionForWorkbookRequest} from '../../workbook/ServerApi/requests/SetAnalyticsExtConnectionForWorkbookRequest';
import { shareContent, ShareContentAction } from '../actions/spaces/ShareContentAction';
import { ShareContentButton, ShareContentButtonProps } from '../spaces/ShareContent/ShareContentButton';
import { MapDispatchToProps } from '../utils/ConnectUtils';

const EINSTEIN_DISCOVERY_CELL_ICON = imgUrl('einstein_discovery_new_connection.svg');
const TABPY_CELL_ICON = imgUrl('tabpy_new_connection.svg');
const RSERVE_CELL_ICON = imgUrl('rserve_new_connection.svg');
const ANALYTICS_EXTENSION_API_CELL_ICON = imgUrl('analytics_extensions_api.svg');
const NO_CONNECTION_ICON = imgUrl('extension.svg');

export interface PublishCompleteDialogOwnProps {
  dismiss: () => void;
  close: () => void;
  onDevicePreviewClick: () => void;
  workbook: IContentInfo<'workbook'>;
  datasources: IDataSourceBase[];
  getDatasourceIconClass: (datasource: IDataSourceBase) => string;
  publishLiveQueryInfo: string;
  shouldDisplayExtensionServiceSection: boolean;
  shouldDisplayDevicePreview: boolean;
  shouldDisplayLiveQueryInfo: boolean;
  shouldDisplayScheduleButton: boolean;
}

export interface PublishCompleteDialogDispatchProps {
  readonly shareContent: (content: IContentInfo) => ShareContentAction;
}

export type PublishCompleteDialogProps = PublishCompleteDialogOwnProps & PublishCompleteDialogDispatchProps;

interface DatasourceProps {
  name: string;
  datasourceIconClass: string;
}

// Function called when analytics extensions dropdown option is changed
function useChangeHandler(connectionList: ListItem[]) {
  const [selectedConnection, setSelectedConnection] = React.useState<ListItem>(null);

  return {
    selectedConnection,
    onChange: React.useCallback((connectionLuid: string) => {
      let selection = connectionList.find(c => c.value === connectionLuid);
      setSelectedConnection(selection);
    }, [connectionList]),
  };
}

function createAnExtDropdownListItem(connection: IAnalyticsConnectionBrief) {

  let icon;
  switch (connection.connection_type) {
    case ConnectionTypes.RSERVE.TAG: {
      icon = RSERVE_CELL_ICON;
      break;
    }
    case ConnectionTypes.TABPY.TAG: {
      icon = TABPY_CELL_ICON;
      break;
    }
    case ConnectionTypes.EINSTEIN.TAG: {
      icon = EINSTEIN_DISCOVERY_CELL_ICON;
      break;
    }
    case ConnectionTypes.GENERIC_API.TAG: {
      icon = ANALYTICS_EXTENSION_API_CELL_ICON;
      break;
    }
    default: {
      icon = NO_CONNECTION_ICON;
      break;
    }
  }

  return (
    <div className="publish-complete-an-ext-dropdown-item">
      <div
        style={{
          width: '10%',
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundImage: icon
        }}/>
      <div>
        {connection.connection_name}
      </div>
    </div>);
}

function useAnExtConnectionListInitializer(props: PublishCompleteDialogOwnProps & PublishCompleteDialogDispatchProps, getAnalyticsConnections: (workbookLuid: string) => Promise<IAnalyticsConnectionMetadata[]>) {
  // Sets up a state for our functional component. In this case, a list of available connection options
  // setAnalyticExtensionsConnectionOptions is a function with signature (IAnalyticsExtConnectionSettings[]) => void
  const [analyticExtensionsConnectionOptions, setAnalyticExtensionsConnectionOptions] = React.useState<IAnalyticsConnectionMetadata[]>([]);
  React.useEffect(
    () => {
      if (props.shouldDisplayExtensionServiceSection) {
        //Uses API call to get list of connections available for the published workbook. Assigns this list to AnalyticsExtensionsConnectionOptions
        getAnalyticsConnections(props.workbook.luid).then(
          setAnalyticExtensionsConnectionOptions
        );
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Returning connection list, which will only be updated if the result of the underlying API call changes
  return React.useMemo(() => {

    const list = analyticExtensionsConnectionOptions.map(connection => ({
      value: connection.connection_luid,
      content: createAnExtDropdownListItem(connection.connection_brief)
    }));

    list.unshift({value: 'none', content: <div>{Messages.publishWorkbookSuccessDialogAnExt_noConnection()}</div>});

    return list;
  }, [analyticExtensionsConnectionOptions]);

}

function useAnExtHasScriptCalcsInitializer(props: PublishCompleteDialogOwnProps & PublishCompleteDialogDispatchProps, getHasScriptCalcs: (workbookLuid: string) => Promise<boolean>) {
  // Sets up a state for our functional component.In this case, a boolean indicating whether workbook has scriptCalcs
  // setHasScriptCalcs is a function with signature (boolean) => void
  const [hasScriptCalcs, setHasScriptCalcs] = React.useState<boolean>(false);
  React.useEffect(
    () => {
      if (props.shouldDisplayExtensionServiceSection) {
        //Uses API call to get whether workbook has script calcs. Assigns this value to hasScriptCalcs
        getHasScriptCalcs(props.workbook.id).then(
          setHasScriptCalcs
        );
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  return hasScriptCalcs;
}

export namespace PublishCompleteDialog {
  export function Title(): string {
    return Messages.publishDatasourceSuccessDialogTitleComplete();
  }

  export function Content(props: PublishCompleteDialogProps) {
    return (
      <div className="tb-workbook-publish-success-modal-with-preview">
        <div className="tb-dialog-body-text">
          <div className="tb-published-items">
            <WorkbookSection {...props} />
            <DatasourceSection {...props} />
          </div>
          <div className="tb-connection-section">
            <AnalyticsConnectionSettingsSection {...props} />
          </div>
          <TableauBridgeBox {...props}/>
        </div>
      </div>
    );
  }

  export function WorkbookSection(props: PublishCompleteDialogProps) {
    return (
      <div className="tb-workbook">
        <div className="tb-workbook-body">
          <div className="tb-published-item">
            <div className="tb-workbook-wrapper">
              <div className="tb-published-icon tb-dialogs-workbook" />
              <div className="tb-published-name">{props.workbook.name}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  async function getAnalyticsConnections(workbookLuid: string) {
    const result: IGetAnalyticsConnectionOptionsForWorkbookResult =
      await WebHttpClientWithErrorToastInstance.sendTagRequest(new GetAnalyticsConnectionOptionsForWorkbookRequest(
        {workbook_luid: workbookLuid}));

    return result.connectionMetadataList;
  }

  async function getHasScriptCalcs(workbookLuid: string) {
    let requestParams = {
      id: workbookLuid
    };
    let result = await WebHttpClientWithErrorToastInstance.sendRequest(new GetHasScriptCalcRequest(requestParams));
    return result as boolean;
  }

  function handleAnExtConnectionApply(connection: ListItem, workbookLuid: string, setAppliedConnection: (connectionApplied: ListItem) => void) {
    //If 'No Connection' is selected, then we want to unset.
    if (connection.value === 'none') {
      WebHttpClientWithErrorToastInstance.sendTagRequest((new RemoveAnalyticsExtConnectionForWorkbookRequest({workbook_luid: workbookLuid})));
      setAppliedConnection(null);
    } else {
      let connectionMapping: IAnalyticsConnectionMapping = {
        workbook_luid: workbookLuid,
        connection_luid: connection.value
      };
      WebHttpClientWithErrorToastInstance.sendTagRequest((new SetAnalyticsExtConnectionForWorkbookRequest(connectionMapping)));
      setAppliedConnection(connection);
    }
  }

  export function AnalyticsConnectionSettingsSection(props: PublishCompleteDialogProps) {
    // First two consts are results of API calls, are assigned this way so that promises can resolve fully.
    const hasScriptCalcs = useAnExtHasScriptCalcsInitializer(props, getHasScriptCalcs);
    const connectionList = useAnExtConnectionListInitializer(props, getAnalyticsConnections);
    // appliedConnection is the connection that has actually been applied, and an API call made.
    const [appliedConnection, setAppliedConnection] = React.useState<ListItem>(null);
    // onChange is the change handler for the connection dropdown.
    // selectedConnection is the currently selected (though maybe not applied) connection
    const {selectedConnection, onChange} = useChangeHandler(connectionList);

    if (!props.shouldDisplayExtensionServiceSection) {
      return null;
    }

    let dropdownValue;

    if (connectionList.length === 0) {
      dropdownValue = Messages.publishWorkbookSuccessDialogAnExt_noConnectionsAvailable();
    } else if (!selectedConnection) {
      dropdownValue = Messages.publishWorkbookSuccessDialogAnExt_selectConnection();
    } else {
      dropdownValue = selectedConnection.value;
    }

    return (
      <div className="publish-complete-an-ext-section" style={{display: hasScriptCalcs ? null : 'none'}}>
        <div style={{display: appliedConnection ? 'none' : null}} className="publish-complete-an-ext-no-connection-selected">
          <div className="tb-dialogs-warning "/>
          <div className="publish-complete-an-ext-no-connection-selected-text">
            {Messages.publishWorkbookSuccessDialogAnExt_noConnectorSelected()}
          </div>
        </div>
        <div className="publish-complete-an-ext-no-connection-warning">
          {Messages.publishWorkbookSuccessDialogAnExt_noConnectionWarning()}
        </div>
        <div className="publish-complete-an-ext-row">
          <SingleSelectDropdown
            className="publish-complete-an-ext-dropdown"
            testId="tb-publish-complete-anext-workbook-options"
            label={Messages.publishWorkbookSuccessDialogAnExt_chooseAConnection()}
            list={<List items={connectionList}/>}
            onChange={onChange}
            type={DropdownButtonType.Outline}
            disabled={false}
            value={dropdownValue}
          />
          <ButtonWidget
            className="publish-complete-an-ext-apply-button"
            testId="tb-publish-complete-anext-workbook-apply-connection"
            buttonType={ButtonType.Outline}
            id="applyWorkbookConnection"
            disabled={connectionList.length === 0 || selectedConnection?.value === appliedConnection?.value}
            handleClick={() => {
              handleAnExtConnectionApply(selectedConnection, props.workbook.luid, setAppliedConnection);
            }}>{Messages.publishWorkbookSuccessDialogAnExt_applyConnection()}</ButtonWidget>
        </div>
      </div>
    );
  }

  export function DevicePreviewLink(props: PublishCompleteDialogProps) {
    if (!props.shouldDisplayDevicePreview) {
      return null;
    }

    return (
      <div className="tb-device-preview">
        <TextLinkWidget
          testId={'tb-device-preview-text'}
          className={'tb-device-preview-text'}
          onClick={props.onDevicePreviewClick}
          kind={TextLinkKind.LowEmphasis}
          tabIndex={0}
        >
          {Messages.publishWorkbookSuccessDialogText_devicePreviewMessage()}
        </TextLinkWidget>
      </div>
    );
  }

  export function DatasourceSection(props: PublishCompleteDialogProps) {
    if (props.datasources.length === 0 && props.shouldDisplayScheduleButton === false) {
      return null;
    }

    return (
      <div className="tb-datasources">
        <span className="tb-datasources-title">{Messages.publishWorkbookSuccessDialogText_datasourcesHeader()}</span>
        <div className="tb-datasources-body">
          {props.datasources.map((item, index) => <Datasource key={index} {...{name: item.name, datasourceIconClass: props.getDatasourceIconClass(item)}}/>)}
          <ScheduleRefreshLink {...props}/>
        </div>
      </div>
    );
  }

  export function Datasource(props: DatasourceProps): JSX.Element {
    const iconClass = `tb-published-icon ${props.datasourceIconClass}`;
    return (
      <div className="tb-published-item">
        <div className="tb-datasource-wrapper">
          <div className={iconClass} />
          <div className="tb-published-name">{props.name}</div>
        </div>
      </div>
    );
  }

  export function ScheduleRefreshLink(props: PublishCompleteDialogProps) {
    if (props.shouldDisplayScheduleButton === false) {
      return null;
    }

    /* eslint-disable jsx-a11y/no-static-element-interactions */
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    /* eslint-disable jsx-a11y/anchor-is-valid */
    return (
      <div className="tb-data-refresh">
        <div className="tb-data-refresh-text">{Messages.publishWorkbookSuccessDialogText_scheduleMessage()}</div>
        {/* Must call props.close here so that this ReactDialog's promise resolves on exit (instead of rejects) and the schedule dialog can open  */}
        {/* See how this dialog is created in PublishWorkbookAction.ts  */}
        <a className="tb-data-refresh-link" onClick={props.close}>{Messages.publishWorkbookSuccessDialogText_scheduleLink()}</a>
      </div>
    );
  }

  export function TableauBridgeBox(props: PublishCompleteDialogProps) {
    if (!props.shouldDisplayLiveQueryInfo) {
      return null;
    }

    return (
      <div className="tb-box tb-publish-details">
        <span>{props.publishLiveQueryInfo}</span>
        &nbsp;
        <Tooltip
          position={TooltipPosition.Right}
          getContent={() => <div>{Messages.publishDatasourceSuccessDialogText_onlineSync_help()}</div>}
        >
          <span style={{ textDecoration: 'underline' }}>
            {Messages.publishDatasourceSuccessDialogText_onlineSync_whatsthis()}
          </span>
        </Tooltip>
      </div>
    );
  }

  export function GetShareContentButtonProps(props: PublishCompleteDialogProps): ShareContentButtonProps {
    /***
    * Must call props.dismiss here so that this ReactDialog's promise rejects on exit (instead of resolves) to prevent the schedule dialog from opening
    * See how this dialog is created in PublishWorkbookAction.ts
    * */
    return {
      handleClick: () => {
        props.dismiss();
        props.shareContent(props.workbook);
      },
      text: Messages.publishWorkbookSuccessDialogButton_share(),
      testId: 'share-icon'
    };
  }

  function Footer(props: PublishCompleteDialogProps) {
    return <div
      className="publish-complete-dialog-footer"
      data-tb-test-id="publish-complete-dialog-footer"
      >
      <DialogFooterButtonsWidget buttons={[ ShareContentButton(GetShareContentButtonProps(props)) ]} testId="publish-complete-dialog-footer-buttons" />
      <DevicePreviewLink {...props} />
    </div>;
  }

  const mapDispatchToProps: MapDispatchToProps<PublishCompleteDialogDispatchProps, PublishCompleteDialogOwnProps> = (dispatch) => bindActionCreators({
    shareContent: shareContent,
  }, dispatch);

  export const ConnectedFooter: React.ComponentClass<PublishCompleteDialogOwnProps> = connect(null, mapDispatchToProps)(Footer);
}
